// @systeme: interface
// La file d'attente hors ligne : un vocal ou un texte capturé sans réseau est gardé sur
// le téléphone (IndexedDB) et part dès que le réseau revient. Rien ne se perd jamais.
// Chaque envoi porte une clé : le serveur ne traite jamais deux fois le même.
import type { ModeCapture } from '@foyer/shared';
import { api, ErreurClient } from './api';

export interface CaptureEnAttente {
  cle: string;
  type: 'texte' | 'audio' | 'photo';
  mode: ModeCapture;
  texte?: string;
  audio?: Blob;
  photo?: Blob;
  /** Pour une photo : dit par l'utilisatrice avant l'envoi (DEC-019). */
  sante?: boolean;
  nom?: string;
  /** Nombre de tentatives d'envoi, pour ne pas s'acharner sur une capture refusée. */
  tentatives: number;
}

/** Le stockage, abstrait pour les tests (mémoire) et le navigateur (IndexedDB). */
export interface Stockage {
  lister(): Promise<CaptureEnAttente[]>;
  ajouter(capture: CaptureEnAttente): Promise<void>;
  retirer(cle: string): Promise<void>;
  remplacer(capture: CaptureEnAttente): Promise<void>;
}

export class StockageMemoire implements Stockage {
  private captures = new Map<string, CaptureEnAttente>();
  async lister() {
    return [...this.captures.values()];
  }
  async ajouter(c: CaptureEnAttente) {
    this.captures.set(c.cle, c);
  }
  async retirer(cle: string) {
    this.captures.delete(cle);
  }
  async remplacer(c: CaptureEnAttente) {
    this.captures.set(c.cle, c);
  }
}

const NOM_BASE = 'foyer';
const NOM_MAGASIN = 'captures_en_attente';

function ouvrirBase(): Promise<IDBDatabase> {
  return new Promise((resoudre, rejeter) => {
    const demande = indexedDB.open(NOM_BASE, 1);
    demande.onupgradeneeded = () => {
      if (!demande.result.objectStoreNames.contains(NOM_MAGASIN))
        demande.result.createObjectStore(NOM_MAGASIN, { keyPath: 'cle' });
    };
    demande.onsuccess = () => resoudre(demande.result);
    demande.onerror = () => rejeter(demande.error ?? new Error('IndexedDB indisponible'));
  });
}

function requete<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resoudre, rejeter) => {
    r.onsuccess = () => resoudre(r.result);
    r.onerror = () => rejeter(r.error ?? new Error('IndexedDB'));
  });
}

export class StockageNavigateur implements Stockage {
  private disponible(): boolean {
    return typeof indexedDB !== 'undefined';
  }
  private async magasin(mode: IDBTransactionMode) {
    const base = await ouvrirBase();
    return base.transaction(NOM_MAGASIN, mode).objectStore(NOM_MAGASIN);
  }
  async lister(): Promise<CaptureEnAttente[]> {
    if (!this.disponible()) return [];
    const tout: unknown = await requete((await this.magasin('readonly')).getAll());
    return Array.isArray(tout) ? tout.filter(estCaptureEnAttente) : [];
  }
  async ajouter(c: CaptureEnAttente) {
    if (!this.disponible()) throw new Error('Le stockage du téléphone est indisponible.');
    await requete((await this.magasin('readwrite')).put(c));
  }
  async retirer(cle: string) {
    if (!this.disponible()) return;
    await requete((await this.magasin('readwrite')).delete(cle));
  }
  async remplacer(c: CaptureEnAttente) {
    if (!this.disponible()) return;
    await requete((await this.magasin('readwrite')).put(c));
  }
}

function estCaptureEnAttente(v: unknown): v is CaptureEnAttente {
  return typeof v === 'object' && v !== null && 'cle' in v && 'type' in v && 'mode' in v;
}

const TENTATIVES_MAX = 20;

export type EtatFile = { enAttente: number; envoiEnCours: boolean };
type Abonne = (etat: EtatFile) => void;

/** La file : ajouter, envoyer ce qui peut l'être, prévenir les écrans. */
export class FileAttente {
  private abonnes = new Set<Abonne>();
  private envoiEnCours = false;
  constructor(
    private readonly stockage: Stockage,
    private readonly envoyer: (c: CaptureEnAttente) => Promise<unknown> = envoyerAuServeur,
  ) {}

  abonner(rappel: Abonne): () => void {
    this.abonnes.add(rappel);
    void this.prevenir();
    return () => this.abonnes.delete(rappel);
  }

  private async prevenir() {
    const enAttente = (await this.stockage.lister()).length;
    for (const a of this.abonnes) a({ enAttente, envoiEnCours: this.envoiEnCours });
  }

  /** Garde la capture, puis tente l'envoi tout de suite. Renvoie vrai si elle est partie. */
  async ajouter(capture: Omit<CaptureEnAttente, 'tentatives'>): Promise<boolean> {
    await this.stockage.ajouter({ ...capture, tentatives: 0 });
    await this.prevenir();
    const envoyees = await this.envoyerTout();
    return envoyees.includes(capture.cle);
  }

  /** Envoie tout ce qui attend, dans l'ordre. S'arrête au premier problème de réseau. */
  async envoyerTout(): Promise<string[]> {
    if (this.envoiEnCours) return [];
    this.envoiEnCours = true;
    const envoyees: string[] = [];
    try {
      for (const capture of await this.stockage.lister()) {
        try {
          await this.envoyer(capture);
          await this.stockage.retirer(capture.cle);
          envoyees.push(capture.cle);
        } catch (erreur) {
          if (erreur instanceof ErreurClient && erreur.code === 'reseau') break; // on réessaiera au retour du réseau
          // Refus du serveur (message vide, quota…) : on n'insiste pas éternellement.
          const tentatives = capture.tentatives + 1;
          if (
            tentatives >= TENTATIVES_MAX ||
            (erreur instanceof ErreurClient && erreur.statut !== undefined && erreur.statut < 500)
          ) {
            await this.stockage.retirer(capture.cle);
          } else {
            await this.stockage.remplacer({ ...capture, tentatives });
          }
        }
      }
    } finally {
      this.envoiEnCours = false;
      await this.prevenir();
    }
    return envoyees;
  }
}

async function envoyerAuServeur(c: CaptureEnAttente): Promise<unknown> {
  if (c.type === 'texte')
    return api.captures.texte({ texte: c.texte ?? '', mode: c.mode, cleClient: c.cle });
  if (c.type === 'photo') {
    if (!c.photo) throw new ErreurClient('photo_vide', 'Photo vide.', 400);
    return api.captures.photo(c.photo, c.sante === true, c.cle, c.nom ?? 'photo');
  }
  if (!c.audio) throw new ErreurClient('audio_vide', 'Vocal vide.', 400);
  return api.captures.audio(c.audio, c.mode, c.cle);
}

let fileGlobale: FileAttente | undefined;
/** La file du navigateur ; se branche sur le retour du réseau. */
export function fileAttente(): FileAttente {
  if (!fileGlobale) {
    fileGlobale = new FileAttente(new StockageNavigateur());
    window.addEventListener('online', () => void fileGlobale?.envoyerTout());
  }
  return fileGlobale;
}

export function nouvelleCle(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const octets = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(octets, (o) => o.toString(16).padStart(2, '0')).join('');
}
